import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { FaThumbsUp } from "react-icons/fa";

import { putProveedor } from "../helpers/rutaProveedores";
import { getLocalidades } from "../helpers/rutaLocalidades";

import "../css/modalformalert.css";

const getId = (value) => {
  if (!value) return "";

  if (typeof value === "object") {
    return value._id || value.id || "";
  }

  const s = String(value);

  return s.length === 24 ? s : "";
};

const normalizarArray = (data, key) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  if (Array.isArray(data?.data?.[key])) return data.data[key];
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const initialForm = {
  codprov: "",
  razonsocial: "",
  domicilio: "",
  telefono: "",
  cuit: "",
  email: "",
  localidad: "",
};

const ModalFormProveedor = ({ proveedor, handleClose, onSaved }) => {
  const prov = proveedor?.proveedor || proveedor?.proveedores || proveedor || {};

  const [formValues, setFormValues] = useState(initialForm);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const [localidades, setLocalidades] = useState({
    data: [],
    loading: true,
    error: "",
  });

  useEffect(() => {
    const localidadId = getId(prov.localidad);

    setFormValues({
      codprov: prov.codprov ?? "",
      razonsocial: prov.razonsocial ?? "",
      domicilio: prov.domicilio ?? "",
      telefono: prov.telefono ?? "",
      cuit: prov.cuit ?? "",
      email: prov.email ?? "",
      localidad: localidadId ?? "",
    });

    setError("");
    setGuardando(false);
  }, [prov]);

  useEffect(() => {
    const cargarLocalidades = async () => {
      try {
        const resp = await getLocalidades();
        const lista = normalizarArray(resp, "localidades");

        setLocalidades({
          data: lista,
          loading: false,
          error: "",
        });
      } catch (e) {
        setLocalidades({
          data: [],
          loading: false,
          error: e?.message || "Error al cargar localidades",
        });
      }
    };

    cargarLocalidades();
  }, []);

  const opcionesLocalidades = useMemo(() => {
    return [...(localidades.data || [])]
      .filter((l) => l?.activo ?? true)
      .sort((a, b) =>
        String(a.localidad || "").localeCompare(
          String(b.localidad || ""),
          "es",
          { sensitivity: "base" }
        )
      );
  }, [localidades.data]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormValues((s) => ({
      ...s,
      [name]:
        name === "razonsocial" || name === "domicilio"
          ? value.toUpperCase()
          : value,
    }));
  };

  const validar = () => {
    if (!prov?._id) return "Falta el ID del proveedor a modificar.";
    if (!formValues.razonsocial.trim()) return "La razón social es obligatoria.";
    if (!formValues.domicilio.trim()) return "El domicilio es obligatorio.";

    if (formValues.codprov) {
      const cod = Number(formValues.codprov);
      if (!Number.isFinite(cod) || cod <= 0) {
        return "El código de proveedor debe ser válido.";
      }
    }

    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const msg = validar();

    if (msg) {
      setError(msg);
      return;
    }

    try {
      setGuardando(true);
      setError("");

      const payload = {
        codprov: formValues.codprov ? Number(formValues.codprov) : undefined,
        razonsocial: formValues.razonsocial.trim().toUpperCase(),
        domicilio: formValues.domicilio.trim().toUpperCase(),
        telefono: formValues.telefono.trim(),
        cuit: formValues.cuit.trim(),
        email: formValues.email.trim().toLowerCase(),
        localidad:
          formValues.localidad && String(formValues.localidad).length === 24
            ? formValues.localidad
            : null,
      };

      const resp = await putProveedor(prov._id, payload);

      if (resp?.ok === false) {
        const backendMsg =
          resp?.err?.message ||
          resp?.error?.err?.message ||
          resp?.error?.message ||
          resp?.error ||
          "No se pudo modificar el proveedor.";

        throw new Error(
          typeof backendMsg === "string"
            ? backendMsg
            : "No se pudo modificar el proveedor."
        );
      }

      setShowSuccessModal(true);

      if (typeof onSaved === "function") {
        onSaved(resp);
      }
    } catch (err) {
      setError(err?.message || "No se pudo modificar el proveedor.");
    } finally {
      setGuardando(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);

    if (typeof handleClose === "function") {
      handleClose();
    }
  };

  if (localidades.loading) {
    return <div className="p-3">Cargando localidades...</div>;
  }

  if (localidades.error) {
    return <div className="p-3 text-danger">{localidades.error}</div>;
  }

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <div className="alert alert-danger mb-3">
              {error}
            </div>
          )}

          <div className="form-group mt-3">
            <label>Cod. Prov</label>
            <input
              type="number"
              className="form-control"
              name="codprov"
              value={formValues.codprov}
              onChange={handleChange}
              placeholder="Auto"
            />
          </div>

          <div className="form-group mt-3">
            <label>Razón Social</label>
            <input
              type="text"
              className="form-control"
              name="razonsocial"
              maxLength="80"
              required
              value={formValues.razonsocial}
              onChange={handleChange}
            />
          </div>

          <div className="form-group mt-3">
            <label>Domicilio</label>
            <input
              type="text"
              className="form-control"
              name="domicilio"
              maxLength="80"
              required
              value={formValues.domicilio}
              onChange={handleChange}
            />
          </div>

          <div className="form-group mt-3">
            <label>Teléfono</label>
            <input
              type="text"
              className="form-control"
              name="telefono"
              maxLength="30"
              value={formValues.telefono}
              onChange={handleChange}
            />
          </div>

          <div className="form-group mt-3">
            <label>CUIT</label>
            <input
              type="text"
              className="form-control"
              name="cuit"
              maxLength="20"
              value={formValues.cuit}
              onChange={handleChange}
            />
          </div>

          <div className="form-group mt-3">
            <label>Email</label>
            <input
              type="email"
              className="form-control"
              name="email"
              maxLength="80"
              value={formValues.email}
              onChange={handleChange}
            />
          </div>

          <div className="form-group mt-3">
            <label>Localidad</label>
            <select
              className="form-control"
              name="localidad"
              value={formValues.localidad}
              onChange={handleChange}
            >
              <option value="">Sin localidad</option>
              {opcionesLocalidades.map((localidad) => (
                <option key={localidad._id} value={localidad._id}>
                  {localidad.localidad}
                </option>
              ))}
            </select>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={guardando}
          >
            Cancelar
          </Button>

          <Button type="submit" variant="dark" disabled={guardando}>
            {guardando ? "Guardando..." : "Guardar"}
          </Button>
        </Modal.Footer>
      </form>

      <Modal show={showSuccessModal} onHide={handleSuccessClose} centered>
        <div className="custom-modal">
          <div className="custom-modal-icon">
            <FaThumbsUp />
          </div>

          <Modal.Body className="custom-modal-body">
            <h4>Info</h4>
            <p>¡Los cambios se guardaron correctamente!</p>
          </Modal.Body>

          <Button className="custom-modal-button" onClick={handleSuccessClose}>
            Aceptar
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default ModalFormProveedor;